import moment from 'moment';
import React from 'react';
import Card from 'react-bootstrap/Card';
import { isAllLocations, getLocationName, getLocationBadgeColor } from '../../../util/locationUtils';

// Constants
const DATE_FORMAT = "ddd Do MMM";

// FIXED: Robust string parsing function
const parseJobInfo = (workString) => {
    try {
        // More robust parsing with fallbacks
        const forIndex = workString.lastIndexOf(' for ');
        const onIndex = workString.indexOf(' on ');
        
        let customerName = 'Customer';
        let serviceName = workString;
        let vehicleInfo = 'Vehicle info not available';
        
        if (forIndex > -1) {
            customerName = workString.substring(forIndex + 5).trim();
            serviceName = workString.substring(0, forIndex).trim();
        }
        
        if (onIndex > -1 && onIndex < forIndex) {
            serviceName = workString.substring(0, onIndex).trim();
            const vehicleAndDealer = workString.substring(onIndex + 4, forIndex).trim();
            vehicleInfo = vehicleAndDealer;
        }
        
        return { customerName, serviceName, vehicleInfo };
    } catch (error) {
        console.error('Error parsing job info:', error);
        return {
            customerName: 'Customer',
            serviceName: workString,
            vehicleInfo: 'Vehicle info not available'
        };
    }
};

const CalendarCard = ({ date, workTable, onDisplayDetails, isDesktop, isExpanded, onCardExpansion, locations, selectedLocationId }) => {
    const showLocationBadge = isAllLocations(selectedLocationId) && Array.isArray(locations) && locations.length > 1;
    // Filter jobs for this specific date
    const dayJobs = workTable.filter(job => job.startDateString === date);
const unsyncedCount = dayJobs.filter(job => 
    !job.isInCalendar && moment(job.startDate).isSameOrAfter(moment().startOf('day'))
).length;
    const isToday = date === moment().format(DATE_FORMAT);

    const handleCardClick = () => {
        onCardExpansion(date);
    };

    const handleJobClick = (job) => {
        onDisplayDetails(job.requestId);
    };

    return (
        <Card className={`day-card ${isExpanded ? 'expanded' : ''} ${isToday ? 'today' : ''}`}>
            <Card.Header className="day-header" onClick={handleCardClick}>
                <div className="day-date">
                    <div className="day-title">
                        {isToday ? `Today - ${date}` : date}
                        {dayJobs.length > 0 && <span className="day-count"> ({dayJobs.length})</span>}
                    </div>
                    <div className="expand-icon">{isExpanded ? '▲' : '▼'}</div>
                </div>
                {unsyncedCount > 0 && (
                    <div className="day-summary">
                        <span className="urgent-indicator">
                            {unsyncedCount} need{unsyncedCount === 1 ? 's' : ''} sync
                        </span>
                    </div>
                )}
            </Card.Header>
            
            {isExpanded && (
                <Card.Body className="day-content">
                    <div className="job-list">
                        {dayJobs.map((job, index) => {
                            // FIXED: Use robust parsing
                            const { customerName, serviceName, vehicleInfo } = parseJobInfo(job.workString);
                            const multiDayInfo = job.isMultiDay ? ` (Day ${job.currentDay} of ${job.totalDays})` : '';
                            
                            return (
                                <div 
                                    key={job.viewKey || `${job.requestId}-${job.serviceId}-${index}`}
                                    className={`job-item ${job.requestType === 'dealer' ? 'dealer' : 'installer'} ${job.isMultiDay ? 'multi-day-job' : ''}`}
                                    onClick={() => handleJobClick(job)}
                                >
                                    <div className="job-header">
                                        <div className="job-info">
                                            <div className="job-description">
                                                <div className="service-name">
                                                    {serviceName} for {customerName}{multiDayInfo}
                                                    {job.workStatus === 'started' && <span className="status-badge started">In Progress</span>}
                                                    {(job.workStatus === 'finished' || job.workStatus === 'completed') && <span className="status-badge completed">Completed</span>}
{!job.isInCalendar && moment(job.startDate).isSameOrAfter(moment().startOf('day')) && <span className="sync-indicator"> ⚠️</span>}                                                </div>
                                                <div className="vehicle-info">
                                                    {vehicleInfo}
                                                </div>
                                                {job.totalServicesInJob > 1 && (
                                                    <div className="job-progress">
                                                        Service {job.currentServiceNumber} of {job.totalServicesInJob} for this job
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="job-details">
                                        Due: {job.dueDateString} • Total: {job.estimate || 0} day{job.estimate !== 1 ? 's' : ''}
                                        {job.isMultiDay && ` • Started: ${moment.utc(job.originalWorkDate).local().format("MMM Do")}`}
                                        {job.assignedTechnicianName && (
                                            <span className="assigned-technician"> • Assigned to {job.assignedTechnicianName}</span>
                                        )}
                                        {showLocationBadge && (job.location_id || job.locationId) && (
                                            <span className="location-badge" style={{
                                                backgroundColor: getLocationBadgeColor(job.location_id || job.locationId, locations),
                                                color: '#fff',
                                                fontSize: '0.65rem',
                                                padding: '2px 6px',
                                                borderRadius: '3px',
                                                marginLeft: '6px',
                                                fontWeight: 600
                                            }}>
                                                {job.locationName || job.location_name || getLocationName((locations || []).find(l => String(l.location_id) === String(job.location_id || job.locationId)))}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </Card.Body>
            )}

            <style>{`
                .day-card {
                    border: 1px solid #e5e7eb !important;
                    border-radius: 8px !important;
                    overflow: hidden;
                    box-shadow: 0 1px 6px rgba(0,0,0,0.08);
                    transition: all 0.3s ease;
                    background: white;
                    margin-bottom: 0;
                }

                .day-card:hover {
                    box-shadow: 0 2px 12px rgba(0,0,0,0.12);
                }

                .day-card.expanded {
                    box-shadow: 0 2px 12px rgba(0,0,0,0.12);
                }

                .day-header {
                    padding: 16px 20px !important;
                    cursor: pointer;
                    transition: background 0.2s ease;
                    border-bottom: 1px solid #e5e7eb !important;
                    background: white !important;
                    border: none !important;
                }

                .day-header:hover {
                    background: #f9fafb !important;
                }

                .day-card.today .day-header {
                    background: #f9fafb !important;
                    position: relative;
                }

                .day-card.today .day-header::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    height: 3px;
                    background: #FF9516;
                }

                .day-date {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                }

                .day-title {
                    font-size: 16px;
                    font-weight: 600;
                    color: #374151;
                }

                .day-count {
                    font-weight: 500;
                    color: #9ca3af;
                }

                .expand-icon {
                    font-size: 14px;
                    color: #6b7280;
                    transition: transform 0.3s ease;
                }

                .day-summary {
                    font-size: 13px;
                    color: #6b7280;
                }

                .urgent-indicator {
                    background: #fff3cd;
                    color: #856404;
                    padding: 2px 6px;
                    border-radius: 4px;
                    font-size: 10px;
                    font-weight: 500;
                    margin-left: 8px;
                    text-transform: uppercase;
                    letter-spacing: 0.3px;
                }

                .day-content {
                    max-height: 0;
                    overflow: hidden;
                    transition: max-height 0.3s ease;
                    border-top: 1px solid #f3f4f6;
                    padding: 0 !important;
                }

                .day-card.expanded .day-content {
                    max-height: 1000px;
                }

                .job-list {
                    padding: 0;
                }

                .job-item {
                    padding: 16px 20px;
                    border-bottom: 1px solid #f3f4f6;
                    transition: all 0.2s ease;
                    border-left: 3px solid transparent;
                    cursor: pointer;
                }

                .job-item:hover {
                    background: #fafafa;
                    transform: translateY(-1px);
                    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
                }

                .job-item:last-child {
                    border-bottom: none;
                }

                .job-item.dealer {
                    border-left-color: #FF9516;
                }

                .job-item.installer {
                    border-left-color: #2E56F6;
                }

                .job-header {
                    display: flex;
                    align-items: flex-start;
                    justify-content: space-between;
                    margin-bottom: 6px;
                }

                .job-info {
                    flex-grow: 1;
                    margin-right: 12px;
                }

                .job-description {
                    line-height: 1.3;
                }

                .service-name {
                    font-size: 15px;
                    font-weight: 600;
                    color: #111827;
                    margin-bottom: 2px;
                    display: flex;
                    align-items: center;
                    flex-wrap: wrap;
                }

                .status-badge {
                    font-size: 10px;
                    font-weight: 600;
                    padding: 2px 8px;
                    border-radius: 4px;
                    text-transform: uppercase;
                    letter-spacing: 0.3px;
                    margin-left: 8px;
                }

                .status-badge.started {
                    background: #fef3c7;
                    color: #92400e;
                }

                .status-badge.completed {
                    background: #dcfce7;
                    color: #166534;
                }

                .sync-indicator {
                    color: #f59e0b;
                    font-size: 14px;
                    margin-left: 4px;
                    opacity: 0.9;
                }

                .vehicle-info {
                    font-size: 13px;
                    font-weight: 500;
                    color: #6b7280;
                    margin-bottom: 2px;
                }

                .job-progress {
                    font-size: 12px;
                    font-weight: 500;
                    color: #9ca3af;
                    font-style: italic;
                }

                .assigned-technician {
                    font-size: 12px;
                    font-weight: 500;
                    color: #059669;
                    background: #ecfdf5;
                    padding: 2px 8px;
                    border-radius: 4px;
                    display: inline;
                    margin-left: 4px;
                }

                .tech-icon {
                    font-size: 11px;
                }

                .multi-day-indicator {
                    color: #6b7280;
                    font-size: 13px;
                    font-weight: 500;
                    margin-left: 8px;
                }

                .job-item.multi-day-job {
                    border-left-width: 4px;
                }

                .job-item.multi-day-job.dealer {
                    background: linear-gradient(90deg, rgba(255, 149, 22, 0.05) 0%, transparent 100%);
                }

                .job-item.multi-day-job.installer {
                    background: linear-gradient(90deg, rgba(46, 86, 246, 0.05) 0%, transparent 100%);
                }

                .job-details {
                    font-size: 13px;
                    color: #6b7280;
                    margin-left: 0;
                }

                /* Mobile adjustments */
                @media (max-width: 768px) {
                    .day-header {
                        padding: 14px 16px !important;
                    }

                    .day-title {
                        font-size: 18px;
                    }

                    .day-summary {
                        font-size: 14px;
                    }

                    .job-item {
                        padding: 18px 16px;
                    }

                    .service-name {
                        font-size: 17px;
                        margin-bottom: 4px;
                    }

                    .vehicle-info {
                        font-size: 15px;
                        margin-bottom: 4px;
                    }

                    .job-progress {
                        font-size: 13px;
                    }

                    .job-details {
                        font-size: 14px;
                        margin-top: 8px;
                    }

                    .sync-indicator {
                        font-size: 16px;
                    }

                    .assigned-technician {
                        font-size: 13px;
                        padding: 3px 10px;
                    }

                    .tech-icon {
                        font-size: 12px;
                    }
                }
            `}</style>
        </Card>
    );
};

export default CalendarCard;